import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { UploadStep, UploadSteps } from './UploadSteps';
import { Note } from '../../components/ui';
import { AppTable, Button, FileUpload, Panel, Tooltip } from '@allygpo/ally-ux';
import { getCoreRowModel, getPaginationRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import * as XLSX from 'xlsx';

// ---------- Types ----------

type RowKind = 'new-group' | 'changed' | 'unchanged';

interface ParsedRow {
  payer: string;
  group: string;
  rowKind: RowKind;
}

const previewColumns: ColumnDef<ParsedRow>[] = [
  { id: 'payer', accessorKey: 'payer', header: 'Insurance payer', enableSorting: false },
  {
    id: 'group',
    header: 'Assigned group',
    enableSorting: false,
    cell: ({ row: { original: r } }) => <>
      {r.rowKind === 'new-group' && (
        <i className="pi pi-plus-circle payer-upload-icon-new payer-upload-row-icon text-2xs u-mr-1 vertical-align-middle flex-shrink-0 text-selected" data-pr-tooltip="Assigned to a new group" data-pr-position="top" />
      )}
      {r.rowKind === 'changed' && (
        <i className="pi pi-pencil payer-upload-icon-changed payer-upload-row-icon text-2xs u-mr-1 vertical-align-middle flex-shrink-0 text-selected" data-pr-tooltip="Moving to a different group" data-pr-position="top" />
      )}
      {r.group}
    </>,
  },
];

interface ParseWarning {
  row: number;
  message: string;
}

interface ParseResult {
  /** Rows that matched an existing payer and will be applied */
  rows: ParsedRow[];
  warnings: ParseWarning[];
  newGroups: string[];
  /** Count of uploaded payers that were skipped (not in existing data) */
  skippedCount: number;
}

export interface UploadFooterActions {
  onDiscard: () => void;
  onApply: () => void;
  canApply: boolean;
  onUploadDifferent: () => void;
}

export interface PayerGroupingUploadProps {
  existingPayers: { insurancePayerPrimary: string; assignedMasterPayorGroup: string }[];
  existingGroups: string[];
  onApply: (
    rows: { insurancePayerPrimary: string; assignedMasterPayorGroup: string }[],
    groups: string[],
  ) => void;
  onUploadStateChange?: (state: UploadFooterActions | null) => void;
}

// ---------- Column-detection helpers ----------

const PAYER_ALIASES = ['payer', 'insurance payer', 'insurancepayerprimary', 'payer name', 'insurance_payer_primary'];
const GROUP_ALIASES = ['master payer', 'master payer group', 'masterpayergroup', 'group', 'master_payer_group', 'payer group'];

const normalize = (s: string) => s.trim().toLowerCase().replace(/[_-]/g, ' ');

const detectColumns = (
  headers: string[],
): { payerIdx: number; groupIdx: number } | null => {
  let payerIdx = -1;
  let groupIdx = -1;

  for (let i = 0; i < headers.length; i++) {
    const h = normalize(headers[i]);
    if (payerIdx === -1 && PAYER_ALIASES.includes(h)) payerIdx = i;
    if (groupIdx === -1 && GROUP_ALIASES.includes(h)) groupIdx = i;
  }

  // Fallback: first two columns
  if (payerIdx === -1 && groupIdx === -1 && headers.length >= 2) {
    return { payerIdx: 0, groupIdx: 1 };
  }

  if (payerIdx === -1 || groupIdx === -1) return null;
  return { payerIdx, groupIdx };
};

// ---------- Parse logic ----------

const MAX_GROUP_LEN = 100;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const parseWorkbook = (
  wb: XLSX.WorkBook,
  existingGroups: string[],
  existingPayerNames: Set<string>,
): ParseResult => {
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const raw: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  if (raw.length === 0) {
    return { rows: [], warnings: [{ row: 0, message: 'File contains no data.' }], newGroups: [], skippedCount: 0 };
  }

  // Detect header row
  const headerRow = raw[0].map(String);
  const cols = detectColumns(headerRow);
  if (!cols) {
    return {
      rows: [],
      warnings: [{ row: 1, message: 'Could not detect "Payer" and "Group" columns. Expected headers like "Insurance Payer" and "Master Payer Group".' }],
      newGroups: [],
      skippedCount: 0,
    };
  }

  const warnings: ParseWarning[] = [];
  const seenPayers = new Map<string, number>(); // lowercase payer → index in rows
  const rows: ParsedRow[] = [];
  const groupSet = new Set(existingGroups.map((g) => g.toLowerCase()));
  const newGroupSet = new Set<string>();
  let skippedCount = 0;

  for (let i = 1; i < raw.length; i++) {
    const payerRaw = String(raw[i][cols.payerIdx] ?? '').trim();
    let groupRaw = String(raw[i][cols.groupIdx] ?? '').trim();

    // Skip fully blank rows
    if (!payerRaw && !groupRaw) continue;

    // Blank payer → skip with warning
    if (!payerRaw) {
      warnings.push({ row: i + 1, message: 'Blank payer name — row skipped.' });
      continue;
    }

    // Skip payers that don't exist in the practice data
    if (!existingPayerNames.has(payerRaw.toLowerCase())) {
      warnings.push({ row: i + 1, message: `"${payerRaw}" is not in your payer data — skipped.` });
      skippedCount += 1;
      continue;
    }

    // Blank group → default
    if (!groupRaw) {
      groupRaw = 'Other/Unmapped';
      warnings.push({ row: i + 1, message: `"${payerRaw}" has no group specified — assigned to Other/Unmapped.` });
    }

    // Truncate long group names
    if (groupRaw.length > MAX_GROUP_LEN) {
      groupRaw = groupRaw.slice(0, MAX_GROUP_LEN);
      warnings.push({ row: i + 1, message: `Group name truncated to ${MAX_GROUP_LEN} characters.` });
    }

    // Track new groups
    if (!groupSet.has(groupRaw.toLowerCase())) {
      groupSet.add(groupRaw.toLowerCase());
      newGroupSet.add(groupRaw);
    }

    // Handle duplicate payers — last row wins
    const lowerPayer = payerRaw.toLowerCase();
    if (seenPayers.has(lowerPayer)) {
      warnings.push({ row: i + 1, message: `Duplicate entry for "${payerRaw}" — using this row instead.` });
      const prevIdx = seenPayers.get(lowerPayer)!;
      rows[prevIdx] = { payer: payerRaw, group: groupRaw, rowKind: 'unchanged' };
    } else {
      seenPayers.set(lowerPayer, rows.length);
      rows.push({ payer: payerRaw, group: groupRaw, rowKind: 'unchanged' });
    }
  }

  if (rows.length === 0 && warnings.length === 0) {
    warnings.push({ row: 0, message: 'File contains no data rows after the header.' });
  }

  return { rows, warnings, newGroups: Array.from(newGroupSet), skippedCount };
};

// ---------- Component ----------

const PayerGroupingUpload: React.FC<PayerGroupingUploadProps> = ({
  existingPayers,
  existingGroups,
  onApply,
  onUploadStateChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fileName, setFileName] = useState('');

  const onUploadStateChangeRef = useRef(onUploadStateChange);
  onUploadStateChangeRef.current = onUploadStateChange;
  const uploadCounterRef = useRef(0);

  // Build a set of known payer names for validation
  const existingPayerNameSet = useMemo(
    () => new Set(existingPayers.map((p) => p.insurancePayerPrimary.toLowerCase())),
    [existingPayers],
  );

  const resetState = useCallback(() => {
    setParseResult(null);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /** Validates and parses one picked or dropped file. */
  const handleFile = useCallback(
    (file: File) => {

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileName(file.name);
        setParseResult({
          rows: [],
          warnings: [{ row: 0, message: `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 10 MB.` }],
          newGroups: [],
          skippedCount: 0,
        });
        return;
      }

      uploadCounterRef.current += 1;
      const thisUpload = uploadCounterRef.current;

      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (uploadCounterRef.current !== thisUpload) return;
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const result = parseWorkbook(wb, existingGroups, existingPayerNameSet);

          // Enrich each row with rowKind by comparing against current payer→group assignments
          const currentGroupMap = new Map(
            existingPayers.map((p) => [p.insurancePayerPrimary.toLowerCase(), p.assignedMasterPayorGroup.toLowerCase()])
          );
          const newGroupsLower = new Set(result.newGroups.map((g) => g.toLowerCase()));
          const enrichedRows: ParsedRow[] = result.rows.map((r) => {
            if (newGroupsLower.has(r.group.toLowerCase())) {
              return { ...r, rowKind: 'new-group' as RowKind };
            }
            const currentGroup = currentGroupMap.get(r.payer.toLowerCase());
            if (!currentGroup || currentGroup !== r.group.toLowerCase()) {
              return { ...r, rowKind: 'changed' as RowKind };
            }
            return { ...r, rowKind: 'unchanged' as RowKind };
          });

          setParseResult({ ...result, rows: enrichedRows });
        } catch {
          setParseResult({
            rows: [],
            warnings: [{ row: 0, message: 'Could not read this file. Make sure it is a valid Excel (.xlsx/.xls) or CSV file and is not password-protected.' }],
            newGroups: [],
            skippedCount: 0,
          });
        }
      };
      reader.readAsArrayBuffer(file);
      // Reset so the same file can be re-selected
    },
    [existingGroups, existingPayerNameSet, existingPayers],
  );

  /** The hidden input behind "Upload a different file": cleared so picking the same file again still fires. */
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleApply = useCallback(() => {
    if (!parseResult) return;

    // Build a map of uploaded assignments (only contains recognized payers)
    const uploadedMap = new Map(
      parseResult.rows.map((r) => [r.payer.toLowerCase(), r.group]),
    );

    // Always merge: only update payers present in the upload
    const finalRows = existingPayers.map((ep) => {
      const uploadedGroup = uploadedMap.get(ep.insurancePayerPrimary.toLowerCase());
      return {
        insurancePayerPrimary: ep.insurancePayerPrimary,
        assignedMasterPayorGroup: uploadedGroup ?? ep.assignedMasterPayorGroup,
      };
    });

    // Build full group list (existing + any new groups from the upload)
    const groupSet = new Set<string>(existingGroups);
    for (const r of finalRows) {
      groupSet.add(r.assignedMasterPayorGroup);
    }

    onApply(finalRows, Array.from(groupSet));
    resetState();
  }, [parseResult, existingPayers, existingGroups, onApply, resetState]);

  useEffect(() => {
    // A file that failed to parse has nothing to apply or discard; the error note offers a
    // drop zone to retry, so the dialog footer stays hidden (same test as `hasErrors` below).
    if (parseResult === null || (parseResult.rows.length === 0 && parseResult.warnings.length > 0)) {
      onUploadStateChangeRef.current?.(null);
    } else {
      onUploadStateChangeRef.current?.({
        onDiscard: resetState,
        onApply: handleApply,
        canApply: parseResult.rows.length > 0,
        onUploadDifferent: () => {
          resetState();
          setTimeout(() => fileInputRef.current?.click(), 0);
        },
      });
    }
    return () => { onUploadStateChangeRef.current?.(null); };
  }, [parseResult, resetState, handleApply]);

  const handleDownloadTemplate = useCallback(() => {
    const templateRows = existingPayers.map((r) => ({
      'Insurance Payer': r.insurancePayerPrimary,
      'Master Payer Group': r.assignedMasterPayorGroup,
    }));

    if (templateRows.length === 0) {
      templateRows.push({ 'Insurance Payer': '', 'Master Payer Group': '' });
    }

    const ws = XLSX.utils.json_to_sheet(templateRows);
    ws['!cols'] = [{ wch: 40 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payer Groupings');
    XLSX.writeFile(wb, 'payer-groupings-template.xlsx');
  }, [existingPayers]);

  const hasErrors =
    parseResult !== null &&
    parseResult.rows.length === 0 &&
    parseResult.warnings.length > 0;


  const previewTable = useReactTable({
    data: parseResult?.rows ?? [],
    columns: previewColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: 20 } },
  });

  return (
    <div className="payer-upload-tab-body">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        aria-label="Upload payer groupings file"
      />

      {/* Instructions as numbered steps, hidden once a file is parsed */}
      {!parseResult && (
        <UploadSteps>
          <UploadStep n={1} title="Download your current assignments"
            action={<Button variant="primary-outline" onClick={handleDownloadTemplate}><i className="pi pi-download" aria-hidden="true" />Download current assignments</Button>}>
            Get an Excel file pre-filled with every payer and its current group.
          </UploadStep>
          <UploadStep n={2} title={<>Edit the <strong>Master Payer Group</strong> column</>}
            action={
              <Panel toggleable collapsed className="setup-own-file"
                header={<span className="flex align-items-center u-gap-2">Uploading your own file?</span>}>
                <div className="u-mt-3 u-py-3 u-px-4 border-t-1 border-default">
                  <p className="payer-upload-own-file-desc u-mt-0 u-mr-0 u-mb-2 u-ml-0 text-xs text-tertiary leading-snug">
                    Your spreadsheet needs two columns with the following headers:
                  </p>
                  <div className="flex flex-column u-gap-1">
                    <div className="payer-upload-col-item flex align-items-center u-gap-1 text-xs text-secondary">
                      <span className="payer-upload-col-name font-semibold text-default">Insurance Payer</span>
                      <span className="payer-upload-col-desc text-tertiary">— payer name from your practice data, e.g. BCBS Preferred, Cigna, Charity Care</span>
                    </div>
                    <div className="payer-upload-col-item flex align-items-center u-gap-1 text-xs text-secondary">
                      <span className="payer-upload-col-name font-semibold text-default">Master Payer Group</span>
                      <span className="payer-upload-col-desc text-tertiary">— the payer mix heading to group under, e.g. Blue Cross Blue Shield, Aetna, Ambetter</span>
                    </div>
                  </div>
                </div>
              </Panel>
            }>
            Change group values in the spreadsheet. New group names are created automatically.
          </UploadStep>
          <UploadStep n={3} title="Upload the modified file"
            action={<FileUpload className="w-full" label="Upload updated file" helperText="CSV or Excel · up to 10 MB · nothing is saved until you review the changes" accept=".xlsx,.xls,.csv"
              onSelect={([file]) => handleFile(file)} onValidationError={(file) => handleFile(file)} />}>
            Only payers in your practice data will be updated. Others are skipped.
          </UploadStep>
        </UploadSteps>
      )}

      {/* Result section — shown after a file is parsed */}
      {parseResult && (
        <>

          {hasErrors ? (
            <div className="flex flex-column u-gap-3">
              <Note tone="error" title={`${fileName ? `“${fileName}”` : 'This file'} couldn’t be used`}>
                {parseResult!.warnings.find((w) => w.row === 0)?.message
                  ?? (parseResult!.skippedCount > 0
                    ? `None of the payers in this file match your practice data. Check that you're uploading a file for the correct account.`
                    : parseResult!.warnings[0]?.message ?? 'Could not parse file.')}
              </Note>
              <FileUpload className="w-full" label="Upload a different file" helperText="CSV or Excel · up to 10 MB · nothing is saved until you review the changes" accept=".xlsx,.xls,.csv"
                onSelect={([file]) => handleFile(file)} onValidationError={(file) => handleFile(file)} />
            </div>
          ) : (
            <div className="payer-upload-result flex flex-column flex-none min-h-0 u-gap-3">
              {/* Summary — collapsible like the warnings row */}
              <Panel toggleable collapsed={false} className="payer-upload-summary"
                header={<span className="flex align-items-center u-gap-2">
                    <i className="pi pi-file" />
                    <span className="payer-upload-summary-file">{fileName}</span>
                </span>}>

                {(() => {
                  const newGroupCount = parseResult.rows.filter((r) => r.rowKind === 'new-group').length;
                  const changedCount  = parseResult.rows.filter((r) => r.rowKind === 'changed').length;
                  const unchangedCount = parseResult.rows.filter((r) => r.rowKind === 'unchanged').length;
                  return (
                    <div className="flex flex-column u-gap-1">
                      <div className="flex align-items-start u-gap-2">
                        <i className="pi pi-check-circle text-success" />
                        <span>
                          <strong>{parseResult.rows.length}</strong> insurance payer{parseResult.rows.length !== 1 ? 's' : ''} recognised from file
                        </span>
                      </div>

                      {newGroupCount > 0 && (
                        <div className="flex align-items-start u-gap-2">
                          <i className="pi pi-plus-circle text-info" />
                          <span>
                            <strong>{newGroupCount}</strong> payer{newGroupCount !== 1 ? 's' : ''} will be assigned to a new group
                            <span className="payer-upload-new-groups-list u-ml-1 font-italic text-tertiary">
                              ({parseResult.newGroups.join(', ')})
                            </span>
                          </span>
                        </div>
                      )}

                      {changedCount > 0 && (
                        <div className="flex align-items-start u-gap-2">
                          <i className="pi pi-pencil text-info" />
                          <span>
                            <strong>{changedCount}</strong> payer{changedCount !== 1 ? 's' : ''} will be moved to a different group
                          </span>
                        </div>
                      )}

                      {unchangedCount > 0 && (
                        <div className="flex align-items-start u-gap-2">
                          <i className="pi pi-check-circle text-success" />
                          <span>
                            <strong>{unchangedCount}</strong> payer{unchangedCount !== 1 ? 's' : ''} already in correct group — no change
                          </span>
                        </div>
                      )}

                      {parseResult.skippedCount > 0 && (
                        <div className="flex align-items-start u-gap-2">
                          <i className="pi pi-minus-circle text-warning" />
                          <span>
                            <strong>{parseResult.skippedCount}</strong> payer{parseResult.skippedCount !== 1 ? 's' : ''} skipped — not found in practice data
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Panel>

              {parseResult.warnings.length > 0 && (
                <Panel toggleable collapsed className="payer-upload-warnings"
                  header={<span className="flex align-items-center u-gap-2">
                      <i className="pi pi-exclamation-triangle text-warning" />
                      {parseResult.warnings.length} warning{parseResult.warnings.length !== 1 ? 's' : ''}
                  </span>}>
                  <ul className="flex flex-column u-gap-1 u-m-0 u-pl-4">
                    {parseResult.warnings.map((w, i) => (
                      <li key={i} className="payer-upload-warning-item">
                        {w.row > 0 && <span className="font-semibold u-mr-1">Row {w.row}:</span>}
                        {w.message}
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              {/* Preview table */}
              <AppTable isBordered={false} ariaLabel="Upload preview" table={previewTable} size="sm" pagination={parseResult.rows.length > 20 ? { rowsPerPageOptions: [20, 50, 100] } : false} />
              <Tooltip position="top" target=".payer-upload-row-icon" />


            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayerGroupingUpload;
